/**
 * Status-bar connection + activity monitor (#268).
 *
 * Two subtle indicators on the right side of the workspace status bar:
 *   - Connection icon — luboo:wifi, color/opacity tracks state:
 *       connected   = normal opacity, --notice-success tint
 *       reconnecting= attention tint, gentle pulse
 *       offline     = low opacity, --notice-critical tint
 *   - Activity dot — small circle that briefly pulses on any data
 *     transfer (WS message in, fetch settle). Idle otherwise.
 *
 * Signals consumed:
 *   - navigator.onLine + window 'online'/'offline' events
 *   - 'luboo:ws-open' / 'luboo:ws-close' (dispatched from
 *     LubooWebSocketManager) — drives the connection state
 *   - 'luboo:activity' (WS message or fetch settle) — pulses the dot
 *
 * Fetch is wrapped once at module load to track in-flight requests
 * and dispatch luboo:activity on each settle. Pulses are rate-limited
 * to ~8/sec so burst traffic doesn't strobe.
 */

// Disk-LED dot: stay-on window per activity event. Extended on each
// new event so a steady stream keeps the dot lit; brief idle gaps
// blink it off. No rate limit — every event matters.
const DOT_STAY_ON_MS = 120;

// Sparkline: 750ms buckets, rolling 12-second window (16 buckets).
// Drawn at canvas-native resolution (set via the canvas width/height
// attributes; CSS scales). Slower scroll than the previous 250ms
// setup so the timeline feels ambient rather than busy.
const BUCKET_MS = 750;
const BUCKET_COUNT = 16;
const SPARKLINE_TICK_MS = 750;

let _connectionEl = null;
let _activityDotEl = null;
let _sparklineEl = null;
let _sparklineCtx = null;
let _connectionState = 'unknown'; // 'connected' | 'reconnecting' | 'offline' | 'unknown'
let _inFlightFetches = 0;
let _lastActivityAt = null;
let _dotOffTimer = null;
let _currentBucketCount = 0;
let _buckets = new Array(BUCKET_COUNT).fill(0);
let _sparklineTickTimer = null;

function _resolveElements() {
  if (!_connectionEl) _connectionEl = document.querySelector('.status-connection');
  if (!_activityDotEl) _activityDotEl = document.querySelector('.status-activity-dot');
  if (!_sparklineEl) {
    _sparklineEl = document.querySelector('.status-activity-sparkline');
    if (_sparklineEl) _sparklineCtx = _sparklineEl.getContext('2d');
  }
}

function _updateConnectionState(nextState) {
  if (nextState === _connectionState) return;
  _connectionState = nextState;
  _resolveElements();
  if (!_connectionEl) return;
  _connectionEl.classList.remove('connected', 'reconnecting', 'offline');
  _connectionEl.classList.add(nextState);
  _connectionEl.setAttribute('data-tooltip', _tooltipForConnection());
}

function _tooltipForConnection() {
  switch (_connectionState) {
    case 'connected':
      return _lastActivityAt
        ? `Connected · last activity ${_relativeTime(_lastActivityAt)}`
        : 'Connected';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'offline':
      return 'Offline';
    default:
      return 'Connection unknown';
  }
}

function _tooltipForActivity() {
  if (_inFlightFetches > 0) {
    return `${_inFlightFetches} request${_inFlightFetches === 1 ? '' : 's'} pending`;
  }
  return _lastActivityAt ? `Idle · last activity ${_relativeTime(_lastActivityAt)}` : 'Idle';
}

function _relativeTime(timestamp) {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

function _onActivity() {
  _lastActivityAt = Date.now();
  _currentBucketCount += 1;
  _resolveElements();

  // Disk-LED dot: every event re-arms the off-timer. Continuous
  // traffic keeps the dot lit; a gap > DOT_STAY_ON_MS turns it off.
  if (_activityDotEl) {
    _activityDotEl.classList.add('pulsing');
    _activityDotEl.setAttribute('data-tooltip', _tooltipForActivity());
  }
  if (_connectionEl) {
    _connectionEl.setAttribute('data-tooltip', _tooltipForConnection());
  }
  if (_dotOffTimer) window.clearTimeout(_dotOffTimer);
  _dotOffTimer = window.setTimeout(() => {
    if (_activityDotEl) _activityDotEl.classList.remove('pulsing');
    _dotOffTimer = null;
  }, DOT_STAY_ON_MS);
}

function _sparklineTick() {
  // Rotate the rolling window: drop oldest, push current bucket.
  _buckets.shift();
  _buckets.push(_currentBucketCount);
  _currentBucketCount = 0;
  _renderSparkline();
}

function _renderSparkline() {
  _resolveElements();
  if (!_sparklineCtx || !_sparklineEl) return;
  const canvas = _sparklineEl;
  const ctx = _sparklineCtx;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const peak = Math.max(1, ..._buckets);
  const barWidth = w / BUCKET_COUNT;
  const accent = _readCssVar('--highlight-color') || '#eab308';
  const textColor = _readCssVar('--text-color') || '#fcfaff';
  ctx.lineCap = 'round';
  for (let i = 0; i < BUCKET_COUNT; i += 1) {
    const count = _buckets[i];
    if (count === 0) {
      // Empty buckets: faint baseline dot so the timeline reads even
      // during long idle stretches.
      ctx.fillStyle = textColor;
      ctx.globalAlpha = 0.12;
      const baselineY = h - 1;
      ctx.fillRect(Math.floor(i * barWidth), baselineY, Math.max(1, Math.floor(barWidth - 1)), 1);
      continue;
    }
    const intensity = count / peak;
    const barHeight = Math.max(2, Math.round(intensity * (h - 2)));
    ctx.fillStyle = accent;
    // Opacity scales with intensity so steady-low traffic reads as
    // softer than peak bursts.
    ctx.globalAlpha = 0.35 + intensity * 0.55;
    ctx.fillRect(
      Math.floor(i * barWidth),
      h - barHeight,
      Math.max(1, Math.floor(barWidth - 1)),
      barHeight,
    );
  }
  ctx.globalAlpha = 1;
  if (_sparklineEl) {
    _sparklineEl.setAttribute('data-tooltip', _tooltipForSparkline(peak));
  }
}

function _tooltipForSparkline(peak) {
  const total = _buckets.reduce((a, b) => a + b, 0);
  if (total === 0) return 'Recent activity: idle';
  return `Recent activity: ${total} events in last ${(BUCKET_COUNT * BUCKET_MS) / 1000}s · peak ${peak}/bucket`;
}

function _readCssVar(name) {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function _wrapFetch() {
  if (typeof window === 'undefined' || !window.fetch || window.fetch.__lubooWrapped) return;
  const originalFetch = window.fetch.bind(window);
  const wrapped = (...args) => {
    _inFlightFetches += 1;
    _resolveElements();
    if (_activityDotEl) _activityDotEl.setAttribute('data-tooltip', _tooltipForActivity());
    return originalFetch(...args).finally(() => {
      _inFlightFetches = Math.max(0, _inFlightFetches - 1);
      _onActivity();
    });
  };
  wrapped.__lubooWrapped = true;
  window.fetch = wrapped;
}

function _wireListeners() {
  window.addEventListener('online', () => {
    _updateConnectionState('connected');
  });
  window.addEventListener('offline', () => {
    _updateConnectionState('offline');
  });
  window.addEventListener('luboo:ws-open', () => {
    if (navigator.onLine) _updateConnectionState('connected');
  });
  window.addEventListener('luboo:ws-close', (e) => {
    if (!navigator.onLine) {
      _updateConnectionState('offline');
    } else if (e.detail?.reconnecting) {
      _updateConnectionState('reconnecting');
    } else {
      _updateConnectionState('offline');
    }
  });
  window.addEventListener('luboo:activity', () => {
    _onActivity();
  });
}

function _init() {
  // Initial state from navigator. WS-open will upgrade once connected.
  // Every shell (user / account / property) now manages a WebSocket, so
  // a real `luboo:ws-open` event WILL fire shortly after page load and
  // flip this to 'connected'. If it doesn't, the badge correctly
  // surfaces "Reconnecting…" — which is the truth.
  _updateConnectionState(navigator.onLine ? 'reconnecting' : 'offline');
  // Kick off the sparkline tick so the rolling window scrolls even
  // during idle periods (gives a moving baseline that reads as "live").
  if (!_sparklineTickTimer) {
    _sparklineTickTimer = window.setInterval(_sparklineTick, SPARKLINE_TICK_MS);
  }
  _renderSparkline();
}

if (typeof window !== 'undefined') {
  _wrapFetch();
  // DOM may not be ready when this module loads — wire listeners + init
  // either now or on DOMContentLoaded, whichever fires later.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      _wireListeners();
      _init();
    });
  } else {
    _wireListeners();
    _init();
  }
}

export const _statusBarMonitor = {
  // exposed for tests / debugging — not part of the public API
  _getState: () => ({
    connection: _connectionState,
    inFlightFetches: _inFlightFetches,
    lastActivityAt: _lastActivityAt,
  }),
};
